import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  CarbonAdminPage,
  CarbonConfirmDialog,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonSelectInput,
  CarbonTechnicalDetails,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonContextFromSearch, carbonWarningLabel, friendlyCarbonError, reportingRevisionStatusLabel as reportingRevisionLabel } from "./corporateCarbon/workflow";

export const CarbonFinalizationPage = () => {
  const token = useTokenOps().account?.accessToken;
  const generation = useRef(0);
  const initialContext = useRef(
    carbonContextFromSearch(window.location.hash || window.location.search),
  );
  const [inventories, setInventories] = useState([]);
  const [inventoryId, setInventoryId] = useState("");
  const [revisions, setRevisions] = useState([]);
  const [revisionId, setRevisionId] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [accepted, setAccepted] = useState([]);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    setInventories([]);
    setInventoryId("");
    setRevisions([]);
    setRevisionId("");
    setReadiness(null);
    setAccepted([]);
    setError("");
    setConfirmOpen(false);
    setFinalizing(false);
    if (!token) return undefined;
    carbonRequest(token, "corporate-carbon/inventories")
      .then(async (values) => {
        if (current !== generation.current) return;
        setInventories(values);
        const requestedInventory = values.find(({ id }) => id === initialContext.current.inventoryId);
        if (requestedInventory) {
          setInventoryId(requestedInventory.id);
          const versionValues = await carbonRequest(token, `corporate-carbon/inventories/${requestedInventory.id}/revisions`);
          if (current !== generation.current) return;
          setRevisions(versionValues);
          if (versionValues.some(({ id }) => id === initialContext.current.revisionId)) {
            setRevisionId(initialContext.current.revisionId);
          }
        }
      })
      .catch((requestError) => {
        if (current === generation.current) setError(friendlyCarbonError(requestError));
      });
    return undefined;
  }, [token]);

  const chooseInventory = async (value) => {
    const current = ++generation.current;
    setConfirmOpen(false); setAccepted([]); setError(""); setFinalizing(false);
    setInventoryId(value);
    setRevisionId("");
    setRevisions([]);
    setReadiness(null);
    if (!value) return;
    try {
      const values = await carbonRequest(token, `corporate-carbon/inventories/${value}/revisions`);
      if (current === generation.current) setRevisions(values);
    } catch (requestError) {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    }
  };

  const chooseRevision = (value) => {
    generation.current += 1;
    setRevisionId(value); setReadiness(null); setAccepted([]);
    setConfirmOpen(false); setError(""); setFinalizing(false);
  };

  const check = async () => {
    if (revisions.find(row => row.id === revisionId)?.status !== 'draft') return;
    const current = generation.current;
    try {
      const value = await carbonRequest(
        token,
        `corporate-carbon/revisions/${revisionId}/finalization/readiness`,
      );
      if (current === generation.current) {
        setReadiness({ ...value, revisionId });
        setAccepted([]);
        setError("");
      }
    } catch (requestError) {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    }
  };

  const finalize = async () => {
    if (!readiness || readiness.revisionId !== revisionId || revisions.find(row => row.id === revisionId)?.status !== 'draft') return;
    const current = generation.current;
    setFinalizing(true);
    try {
      await carbonRequest(
        token,
        `corporate-carbon/revisions/${revisionId}/finalization`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      );
      if (current === generation.current) {
        setError("");
        setConfirmOpen(false);
        setReadiness(null);
        setRevisions(rows => rows.map(row => row.id === revisionId ? { ...row, status: 'finalized' } : row));
      }
    } catch (requestError) {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    } finally {
      if (current === generation.current) setFinalizing(false);
    }
  };

  return (
    <CarbonAdminPage
      title="Carbon finalization"
      description="Lock the declared reporting data before calculating its Scope 1 and Scope 2 emissions."
    >
      {error && <Typography color="error" role="alert">{error}</Typography>}
      <CarbonSection title="Choose what to finalize" description="Checks declared activity data, boundary warnings and linked supporting evidence for contractual electricity and supplier-declared heat factors. Factor matching is checked when calculating with the selected published release.">
        <CarbonForm aria-label="Revision readiness form" onSubmit={(event) => { event.preventDefault(); check(); }}>
          <CarbonSelectInput choices={inventories} choiceText={(row) => String(row.reportingYear)} label="Reporting year" name="inventoryId" value={inventoryId} onChange={(event) => chooseInventory(event.target.value)} />
          <CarbonSelectInput choices={revisions} choiceText={reportingRevisionLabel} label="Reporting revision" name="revisionId" value={revisionId} onChange={(event) => chooseRevision(event.target.value)} />
          {revisionId && revisions.find((row) => row.id === revisionId)?.status !== "draft" ? <Typography>This reporting data is read-only. Create a correction to change its inputs.</Typography> : null}
          <CarbonFormToolbar><Button color="primary" disabled={!revisionId || revisions.find((row) => row.id === revisionId)?.status !== "draft"} type="submit" variant="contained">Check readiness</Button></CarbonFormToolbar>
        </CarbonForm>
      </CarbonSection>
      {readiness && (
        <CarbonSection title="Finalization checklist">
          <section aria-label="Finalization checklist">
            <ul aria-label="Readiness checks">
              {(readiness.checks || []).map((item) => <li key={item.code}>
                <Typography>{item.label}: {item.status === "passed" ? "Passed" : item.status === "failed" ? "Failed" : "Not checked"}{item.message ? ` · ${item.message}` : ""}</Typography>
              </li>)}
            </ul>
            {(readiness.missingEvidence || []).map(item => <Typography role="alert" key={item.activityId}>Supporting evidence required: {item.siteCode} · {item.sourceName}</Typography>)}
            {readiness.blockingErrors.map((code) => <Typography color="error" key={code}>{friendlyCarbonError({ message: code })}</Typography>)}
            {readiness.acknowledgementWarnings.map((code) => (
              <FormControlLabel
                key={code}
                control={(
                  <Checkbox
                    checked={accepted.includes(code)}
                    onChange={(_, checked) => setAccepted(checked ? [...accepted, code] : accepted.filter((item) => item !== code))}
                  />
                )}
                label={carbonWarningLabel(code)}
              />
            ))}
            <CarbonTechnicalDetails>
              <Typography>The calculation can be started after this reporting revision has been finalized.</Typography>
            </CarbonTechnicalDetails>
            <Button
              color="primary"
              disabled={readiness.blockingErrors.length > 0 || accepted.length !== readiness.acknowledgementWarnings.length}
              onClick={() => setConfirmOpen(true)}
              variant="contained"
            >
              Finalize reporting revision
            </Button>
          </section>
        </CarbonSection>
      )}
      <CarbonConfirmDialog
        confirmLabel={finalizing ? "Finalizing…" : "Finalize"}
        description="This reporting revision will become immutable before calculation. Future corrections must be made in a new revision."
        disabled={finalizing}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={finalize}
        open={confirmOpen}
        title="Finalize reporting revision?"
      />
    </CarbonAdminPage>
  );
};

const resource = () => (
  <Resource name="carbonFinalization" options={{ label: "Finalization", menuGroup: "Carbon Accounting" }} list={CarbonFinalizationPage} />
);
export default resource;
